import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
  Search,
  X,
  TrendingUp,
  Building2,
  Package,
  User,
  Clock,
  Calendar,
  Loader2,
  ChevronLeft,
  Sparkles,
  RefreshCw,
} from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { useAppStore } from "../store/useAppStore";
import { catalogService } from "../services/catalogService";
import { companiesService } from "../services/companiesService";
import { productsService } from "../services/productsService";
import { representativesService } from "../services/representativesService";
import { eventPackService } from "../services/eventPackService";
import { sessionsService } from "../services/sessionsService";
import type { Company, Product, Representative, EventSession } from "../types";
import { PageShell } from "../components/PageShell";
import { GlassCard } from "../components/GlassCard";
import { CompanyAvatar } from "../components/CompanyAvatar";
import { ProductCard } from "../components/ProductCard";
import { RepresentativeCard } from "../components/RepresentativeCard";
import { EmptyState } from "../components/EmptyState";
import { formatCompanyLocation, isSharedHall } from "../utils/locationLabel";
import { formatCategoriesDisplay } from "../utils/categoryLabel";

type SearchCategory = "all" | "companies" | "products" | "representatives" | "sessions";

interface SearchResult {
  type: "company" | "product" | "service" | "representative" | "session";
  id: string;
  title: string;
  subtitle: string;
  data: any;
}

function pickRandom<T>(items: T[], count: number): T[] {
  if (items.length <= count) return [...items];
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j]!, copy[i]!];
  }
  return copy.slice(0, count);
}

export function SearchPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedEventId, setNavbarVisible } = useAppStore();

  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [category, setCategory] = useState<SearchCategory>("all");
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [popularQueries, setPopularQueries] = useState<string[]>([]);
  const [discover, setDiscover] = useState<SearchResult[]>([]);
  const [discoverLoading, setDiscoverLoading] = useState(false);
  const [discoverSeed, setDiscoverSeed] = useState(0);

  // Debounced search
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const [catalogResults, setCatalogResults] = useState<{
    companies: Company[];
    products: Array<Product & { companyName?: string }>;
    services: Array<{ id: string; companyId: string; name: string; category: string; companyName?: string }>;
    representatives: Array<Representative & { companyName?: string }>;
  } | null>(null);
  const [sessionResults, setSessionResults] = useState<EventSession[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [localCompanies, setLocalCompanies] = useState<Company[]>([]);
  const [localProducts, setLocalProducts] = useState<Array<Product & { companyName?: string }>>([]);
  const [localReps, setLocalReps] = useState<Array<Representative & { companyName?: string }>>([]);
  const lastRecordedRef = useRef("");

  useEffect(() => {
    if (!selectedEventId) {
      setLocalCompanies([]);
      setLocalProducts([]);
      setLocalReps([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const [companies, products, reps] = await Promise.all([
        companiesService.list(selectedEventId).catch(() => [] as Company[]),
        productsService.getByEvent(selectedEventId).catch(() => [] as Product[]),
        representativesService.getByEvent(selectedEventId).catch(
          () => [] as Array<Representative & { companyName?: string }>,
        ),
      ]);
      if (cancelled) return;
      const names = new Map(companies.map((c) => [c.id, c.name]));
      setLocalCompanies(companies);
      setLocalProducts(
        products.map((p) => ({
          ...p,
          companyName: names.get(p.companyId),
        })),
      );
      setLocalReps(reps);
    })();
    return () => {
      cancelled = true;
    };
  }, [i18n.language, selectedEventId]);

  useEffect(() => {
    if (!selectedEventId) {
      setSearchHistory([]);
      setPopularQueries([]);
      return;
    }
    let cancelled = false;
    catalogService.getSearchSuggestions(selectedEventId).then((data) => {
      if (cancelled) return;
      setSearchHistory(data.recent);
      setPopularQueries(data.popular);
    });
    return () => {
      cancelled = true;
    };
  }, [i18n.language, selectedEventId]);

  useEffect(() => {
    if (!selectedEventId || query.trim()) {
      setDiscover([]);
      return;
    }
    if (category === "sessions") {
      setDiscover([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setDiscoverLoading(true);
      try {
        const companyNameById = new Map<string, string>();
        const companies = await companiesService.list(selectedEventId).catch(() => [] as Company[]);
        for (const c of companies) companyNameById.set(c.id, c.name);

        const [products, reps] = await Promise.all([
          eventPackService.listProducts(selectedEventId).catch(() => []),
          eventPackService.listRepresentatives(selectedEventId).catch(() => []),
        ]);

        const companyPicks = pickRandom(companies, category === "companies" ? 6 : 2).map(
          (c): SearchResult => ({
            type: "company",
            id: c.id,
            title: c.name,
            subtitle: [formatCategoriesDisplay(c.category), formatCompanyLocation(c)].filter(Boolean).join(" · "),
            data: c,
          }),
        );

        const productPicks = pickRandom(products, category === "products" ? 6 : 2).map((p) => {
          const product: Product & { companyName?: string } = {
            id: p.id,
            companyId: p.companyId,
            eventId: p.eventId,
            name: p.name,
            description: p.description,
            category: p.category,
            images: p.images ?? [],
            tags: p.tags ?? [],
            qrCode: p.qrCode,
            barcode: p.barcode,
            price: p.price,
            isFavorite: p.isFavorite,
            favoritedAt: p.favoritedAt,
            companyName: companyNameById.get(p.companyId),
          };
          return {
            type: "product" as const,
            id: p.id,
            title: p.name,
            subtitle: product.companyName || p.category,
            data: product,
          };
        });

        const repPicks = pickRandom(reps, category === "representatives" ? 6 : 2).map((r) => {
          const companyName = companyNameById.get(r.companyId);
          const mapped: Representative & { companyName?: string } = {
            id: r.id,
            companyId: r.companyId,
            eventId: r.eventId,
            fullName: r.fullName,
            position: r.position,
            photo: r.photoUrl,
            bio: r.bio,
            phone: r.phone,
            email: r.email,
            telegram: r.telegram,
            badgeQrCode: r.badgeQrCode || "",
            badgeBarcode: r.badgeBarcode,
            companyName,
          };
          return {
            type: "representative" as const,
            id: r.id,
            title: r.fullName,
            subtitle: `${r.position}${companyName ? ` · ${companyName}` : ""}`,
            data: mapped,
          };
        });

        let items: SearchResult[] = [];
        if (category === "companies") items = companyPicks;
        else if (category === "products") items = productPicks;
        else if (category === "representatives") items = repPicks;
        else {
          items = pickRandom([...companyPicks, ...productPicks, ...repPicks], 6);
        }

        if (!cancelled) setDiscover(items);
      } catch {
        if (!cancelled) setDiscover([]);
      } finally {
        if (!cancelled) setDiscoverLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedEventId, query, category, discoverSeed]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
      if (query) {
        setSearchParams({ q: query });
      } else {
        setSearchParams({});
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, setSearchParams]);

  // Persist intentional queries after user pauses typing (avoids counting every keystroke).
  useEffect(() => {
    if (!selectedEventId) return;
    const q = query.trim();
    if (q.length < 2) return;
    const timer = window.setTimeout(() => {
      if (lastRecordedRef.current === q.toLocaleLowerCase("ru-RU")) return;
      lastRecordedRef.current = q.toLocaleLowerCase("ru-RU");
      void catalogService.recordSearchQuery(selectedEventId, q).then((data) => {
        if (data.recent.length) setSearchHistory(data.recent);
        setPopularQueries(data.popular);
      });
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [query, selectedEventId]);

  useEffect(() => {
    if (!selectedEventId || !debouncedQuery.trim()) {
      setCatalogResults(null);
      setSessionResults([]);
      return;
    }
    if (category === "sessions") {
      setCatalogResults(null);
    }

    let cancelled = false;
    (async () => {
      setSearchLoading(true);
      try {
        const tasks: Promise<void>[] = [];

        if (category !== "sessions") {
          tasks.push(
            catalogService.search(selectedEventId, debouncedQuery).then((result) => {
              if (!cancelled) setCatalogResults(result);
            }),
          );
        } else {
          setCatalogResults(null);
        }

        if (category === "all" || category === "sessions") {
          tasks.push(
            sessionsService
              .search(debouncedQuery, selectedEventId)
              .then((sessions) => {
                if (!cancelled) setSessionResults(sessions);
              })
              .catch(() => {
                if (!cancelled) setSessionResults([]);
              }),
          );
        } else {
          setSessionResults([]);
        }

        await Promise.all(tasks);
      } catch {
        if (!cancelled) {
          setCatalogResults((prev) => prev ?? { companies: [], products: [], services: [], representatives: [] });
          setSessionResults([]);
        }
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [i18n.language, selectedEventId, debouncedQuery, category]);

  // Search logic: catalog API + local company substring fallback (Meili can miss)
  const results = useMemo((): SearchResult[] => {
    if (!debouncedQuery.trim()) return [];

    const q = debouncedQuery.toLowerCase();
    const allResults: SearchResult[] = [];
    const hideHall = isSharedHall(localCompanies);

    if (category === "all" || category === "companies") {
      const seen = new Set<string>();
      const pushCompany = (c: Company) => {
        if (seen.has(c.id)) return;
        seen.add(c.id);
        allResults.push({
          type: "company",
          id: c.id,
          title: c.name,
          subtitle: [
            formatCategoriesDisplay(c.category),
            formatCompanyLocation({ ...c, hideHall }),
          ]
            .filter(Boolean)
            .join(" · "),
          data: c,
        });
      };

      for (const c of catalogResults?.companies ?? []) pushCompany(c);

      // Substring fallback over full exhibitor list (fixes stale Meili / typo gaps)
      for (const c of localCompanies) {
        const cats = formatCategoriesDisplay(c.category).toLowerCase();
        if (
          c.name.toLowerCase().includes(q) ||
          cats.includes(q) ||
          c.category.toLowerCase().includes(q) ||
          c.booth.toLowerCase().includes(q) ||
          (c.shortAbout || "").toLowerCase().includes(q)
        ) {
          pushCompany(c);
        }
      }
    }

    if (category === "all" || category === "products") {
      const seen = new Set<string>();
      const pushProduct = (p: Product & { companyName?: string }) => {
        if (seen.has(p.id)) return;
        seen.add(p.id);
        allResults.push({
          type: "product",
          id: p.id,
          title: p.name,
          subtitle: p.companyName ?? "",
          data: p,
        });
      };
      for (const p of catalogResults?.products ?? []) pushProduct(p);
      for (const p of localProducts) {
        const hay = [
          p.name,
          p.description,
          p.category,
          ...(p.tags ?? []),
          p.companyName ?? "",
        ]
          .join(" ")
          .toLowerCase();
        if (hay.includes(q)) pushProduct(p);
      }
    }

    if (category === "all" || category === "representatives") {
      const seen = new Set<string>();
      const pushRep = (r: Representative & { companyName?: string }) => {
        if (seen.has(r.id)) return;
        seen.add(r.id);
        allResults.push({
          type: "representative",
          id: r.id,
          title: r.fullName,
          subtitle: `${r.position || ""}${r.companyName ? ` · ${r.companyName}` : ""}`.trim(),
          data: r,
        });
      };
      for (const r of catalogResults?.representatives ?? []) pushRep(r);
      for (const r of localReps) {
        const hay = [r.fullName, r.position, r.bio, r.companyName, r.phone, r.email]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (hay.includes(q)) pushRep(r);
      }
    }

    if (category === "all" || category === "sessions") {
      sessionResults.forEach((s) => {
        allResults.push({
          type: "session",
          id: s.id,
          title: s.title,
          subtitle: new Date(s.startTime).toLocaleDateString(i18n.language, {
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
          }),
          data: s,
        });
      });
    }

    return allResults;
  }, [
    debouncedQuery,
    category,
    catalogResults,
    i18n.language,
    sessionResults,
    localCompanies,
    localProducts,
    localReps,
  ]);

  // Group results by type
  const groupedResults = useMemo(() => {
    const groups: Record<string, SearchResult[]> = {
      company: [],
      product: [],
      service: [],
      representative: [],
      session: [],
    };
    results.forEach((r) => groups[r.type].push(r));
    return groups;
  }, [results]);

  const handleSearch = useCallback(
    (searchQuery: string) => {
      const q = searchQuery.trim();
      setQuery(q);
      if (!selectedEventId || q.length < 2) return;
      lastRecordedRef.current = q.toLocaleLowerCase("ru-RU");
      void catalogService.recordSearchQuery(selectedEventId, q).then((data) => {
        if (data.recent.length) setSearchHistory(data.recent);
        setPopularQueries(data.popular);
      });
    },
    [selectedEventId],
  );

  const handleResultClick = (result: SearchResult) => {
    if (result.type === "company") {
      navigate(`/companies/${result.id}`);
    } else if (result.type === "product") {
      navigate(`/products/${result.id}`);
    } else if (result.type === "service") {
      navigate(`/companies/${result.data.companyId}/products`);
    } else if (result.type === "representative") {
      navigate(`/representatives/${result.id}`);
    } else if (result.type === "session") {
      navigate(`/schedule/${result.id}`);
    }
  };

  const clearHistory = () => {
    setSearchHistory([]);
    if (selectedEventId) void catalogService.clearRecentSearches(selectedEventId);
  };

  const showEmpty = debouncedQuery.trim() && results.length === 0 && !searchLoading;
  const showIdle = !query.trim();
  const showHistory = showIdle && searchHistory.length > 0;
  const showTrending = showIdle && popularQueries.length > 0;
  const showDiscover =
    showIdle &&
    category !== "sessions" &&
    (discoverLoading || discover.length > 0);

  return (
    <PageShell noPadding>
      {/* Search Header */}
      <div className="sticky top-0 z-10 bg-liquid-glass-panel border-b border-white/[0.08] safe-top-pad">
        <div className="px-4 pt-3 pb-3 flex items-center gap-2">
          <button
            type="button"
            aria-label={t("common.back")}
            onClick={() => {
              const idx = (window.history.state as { idx?: number } | null)?.idx;
              if (typeof idx === "number" && idx > 0) navigate(-1);
              else navigate("/home");
            }}
            className="flex items-center justify-center w-9 h-9 -ml-1 rounded-full active:bg-black/5 transition-colors flex-shrink-0"
          >
            <ChevronLeft size={22} className="text-gray-700" />
          </button>
          <div className="relative flex-1 min-w-0">
            <Search size={20} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearch(query);
                }
              }}
              onFocus={() => setNavbarVisible(false)}
              onBlur={() => setNavbarVisible(true)}
              placeholder={t("search.placeholder")}
              className="w-full h-11 pl-11 pr-10 rounded-2xl bg-gray-100/80 text-[15px] text-gray-900 placeholder:text-gray-400 outline-none focus:ring-2 focus:ring-indigo-200 transition-shadow"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full transition-colors"
              >
                <X size={16} className="text-gray-500" />
              </button>
            )}
          </div>
        </div>

        {/* Category Filters */}
        <div className="flex gap-2 px-4 pb-3 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
          {[
            { value: "all" as SearchCategory, label: t("search.cat_all"), icon: Search },
            { value: "companies" as SearchCategory, label: t("search.cat_companies"), icon: Building2 },
            { value: "products" as SearchCategory, label: t("search.cat_products"), icon: Package },
            { value: "representatives" as SearchCategory, label: t("search.cat_people"), icon: User },
            { value: "sessions" as SearchCategory, label: t("search.cat_sessions"), icon: Calendar },
          ].map((cat) => {
            const Icon = cat.icon;
            const isActive = category === cat.value;
            return (
              <button
                key={cat.value}
                onClick={() => setCategory(cat.value)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-medium whitespace-nowrap transition-all ${
                  isActive
                    ? "bg-indigo-500 text-white shadow-lg shadow-indigo-500/20"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                <Icon size={14} />
                {cat.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-4 pt-4 pb-24">
        {searchLoading && debouncedQuery.trim() && results.length === 0 && (
          <div className="flex justify-center py-12 text-gray-400">
            <Loader2 size={28} className="animate-spin" />
          </div>
        )}

        {/* Search Results */}
        {results.length > 0 && (
          <div className="space-y-5">
            {/* Companies */}
            {groupedResults.company.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <h3 className="text-[14px] text-gray-500 mb-2 px-1">
                  {t("search.section_companies", { n: groupedResults.company.length })}
                </h3>
                <div className="space-y-2">
                  {groupedResults.company.map((result) => (
                    <GlassCard
                      key={result.id}
                      variant="default"
                      padding="sm"
                      className="cursor-pointer active:scale-[0.98] transition-transform"
                      onClick={() => handleResultClick(result)}
                    >
                      <div className="flex items-center gap-3">
                        <CompanyAvatar name={result.title} size="md" />
                        <div className="flex-1 min-w-0">
                          <p className="text-[15px] text-gray-900 truncate">{result.title}</p>
                          <p className="text-[12px] text-gray-500 truncate">{result.subtitle}</p>
                        </div>
                      </div>
                    </GlassCard>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Products */}
            {groupedResults.product.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05, duration: 0.3 }}
              >
                <h3 className="text-[14px] text-gray-500 mb-2 px-1">
                  {t("search.section_products", { n: groupedResults.product.length })}
                </h3>
                <div className="space-y-2.5">
                  {groupedResults.product.map((result) => (
                    <ProductCard
                      key={result.id}
                      product={result.data}
                      variant="full"
                      companyName={result.subtitle || undefined}
                    />
                  ))}
                </div>
              </motion.div>
            )}

            {/* Representatives */}
            {groupedResults.representative.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.3 }}
              >
                <h3 className="text-[14px] text-gray-500 mb-2 px-1">
                  {t("search.section_reps", { n: groupedResults.representative.length })}
                </h3>
                <div className="space-y-2">
                  {groupedResults.representative.map((result) => (
                    <RepresentativeCard
                      key={result.id}
                      representative={result.data}
                      compact
                    />
                  ))}
                </div>
              </motion.div>
            )}

            {/* Sessions */}
            {groupedResults.session.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15, duration: 0.3 }}
              >
                <h3 className="text-[14px] text-gray-500 mb-2 px-1">
                  {t("search.section_sessions", { n: groupedResults.session.length })}
                </h3>
                <div className="space-y-2">
                  {groupedResults.session.map((result) => (
                    <GlassCard
                      key={result.id}
                      variant="default"
                      padding="sm"
                      className="cursor-pointer active:scale-[0.98] transition-transform"
                      onClick={() => handleResultClick(result)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center flex-shrink-0">
                          <Calendar size={18} className="text-indigo-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[14px] text-gray-900 line-clamp-2">{result.title}</p>
                          <p className="text-[12px] text-gray-500 mt-0.5">{result.subtitle}</p>
                        </div>
                      </div>
                    </GlassCard>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        )}

        {/* Empty State */}
        {showEmpty && (
          <EmptyState
            icon={<Search size={32} />}
            title={t("search.empty_title")}
            description={t("search.empty_desc", { query: debouncedQuery })}
          />
        )}

        {/* Idle: trending / recent / real discover cards */}
        {showIdle && (
          <div className="space-y-5">
            {showTrending && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                <h3 className="text-[15px] text-gray-900 mb-3 flex items-center gap-2">
                  <TrendingUp size={16} className="text-gray-400" />
                  {t("search.trending")}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {popularQueries.map((trend) => (
                    <button
                      key={trend}
                      type="button"
                      onClick={() => handleSearch(trend)}
                      className="px-3.5 py-2 rounded-xl bg-indigo-50 text-[13px] text-indigo-700 active:bg-indigo-100"
                    >
                      {trend}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {showHistory && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[15px] text-gray-900 flex items-center gap-2">
                    <Clock size={16} className="text-gray-400" />
                    {t("search.recent")}
                  </h3>
                  <button type="button" onClick={clearHistory} className="text-[13px] text-indigo-500">
                    {t("search.clear")}
                  </button>
                </div>
                <div className="space-y-1">
                  {searchHistory.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => handleSearch(item)}
                      className="w-full text-left px-3 py-2.5 rounded-xl active:bg-gray-50 transition-colors"
                    >
                      <p className="text-[14px] text-gray-700">{item}</p>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {showDiscover && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[15px] text-gray-900 flex items-center gap-2">
                    <Sparkles size={16} className="text-amber-500" />
                    {t("search.discover_title")}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setDiscoverSeed((n) => n + 1)}
                    className="text-[13px] text-indigo-500 flex items-center gap-1"
                  >
                    <RefreshCw size={13} />
                    {t("search.discover_refresh")}
                  </button>
                </div>
                {discoverLoading ? (
                  <div className="flex justify-center py-8 text-gray-400">
                    <Loader2 size={24} className="animate-spin" />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {discover.map((item) => {
                      if (item.type === "product") {
                        return (
                          <ProductCard
                            key={`d-${item.id}`}
                            product={item.data}
                            variant="full"
                            companyName={item.subtitle || undefined}
                          />
                        );
                      }
                      if (item.type === "representative") {
                        return (
                          <RepresentativeCard
                            key={`d-${item.id}`}
                            representative={item.data}
                            compact
                          />
                        );
                      }
                      return (
                        <GlassCard
                          key={`d-${item.id}`}
                          variant="default"
                          padding="sm"
                          className="cursor-pointer active:scale-[0.98] transition-transform"
                          onClick={() => handleResultClick(item)}
                        >
                          <div className="flex items-center gap-3">
                            <CompanyAvatar name={item.title} size="md" />
                            <div className="flex-1 min-w-0">
                              <p className="text-[15px] text-gray-900 truncate">{item.title}</p>
                              <p className="text-[12px] text-gray-500 truncate">{item.subtitle}</p>
                            </div>
                          </div>
                        </GlassCard>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            )}

            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => navigate("/scan")}
                className="flex-1 h-10 rounded-xl bg-indigo-500 text-white text-[13px] font-medium active:bg-indigo-600"
              >
                {t("search.idle_scan")}
              </button>
              <button
                type="button"
                onClick={() => navigate("/map")}
                className="flex-1 h-10 rounded-xl bg-gray-100 text-gray-700 text-[13px] font-medium active:bg-gray-200"
              >
                {t("search.idle_map")}
              </button>
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
}
